import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { DocumentFilesByDocumentIdResponseRow } from '@risksmart-app/trpc/src/types';
import type { GetDocumentFilesByDocumentIdQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { mapTRPCRefetch } from 'src/utils';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

/**
 * Maps TRPC document files data to match the GraphQL query structure
 */
function mapTrpcDocumentFilesToGraphQLQuery(
  trpcData: DocumentFilesByDocumentIdResponseRow[]
): GetDocumentFilesByDocumentIdQuery {
  return {
    document_file: trpcData,
  };
}

export const useGetDocumentFilesByDocumentId = (
  documentId: string,
  skip?: boolean
) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();

  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.documentFile.documentFilesByDocumentId.queryOptions({
      documentId,
    }),

    enabled: trpcEnabled && !skip, // Only enable TRPC query when flag is true
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcEnabled, trpcError, addNotification]);

  // Transform TRPC data to match the same structure as GraphQL query
  const data = trpcData
    ? mapTrpcDocumentFilesToGraphQLQuery(trpcData)
    : undefined;

  return {
    loading: trpcLoading,
    data,
    refetch: () =>
      mapTRPCRefetch(trpcRefetch, mapTrpcDocumentFilesToGraphQLQuery),
    error: trpcError,
  };
};
